<script setup>
import { ref, computed, onMounted } from 'vue'

import layoutShell from '@/components/layout/layoutShell.vue'
import LayoutFooter from '@/components/layout/layoutFooter.vue'
import AppDialog from '@/components/ui/AppDialog.vue'
import api from '@/api'
import { useUserStore } from '@/stores/user'
import { useNotify } from '@/services/useNotify'
import { useI18n } from '@/i18n'
import { useConfirmDelete } from '@/composables/useConfirmDelete'

const user = useUserStore()
const notify = useNotify()
const { t } = useI18n()
const { confirmDelete } = useConfirmDelete()

const feeds = ref([])
const loading = ref(false)

const showDialog = ref(false)
const isAdding = ref(true)
const saving = ref(false)
const editingFeed = ref({ url: '', description: '' })

const columns = [
  { field: 'id',          header: t('news.sources.colId'),          sortable: true,  default: false },
  { field: 'url',         header: t('news.sources.colUrl'),         sortable: true,  default: true  },
  { field: 'description', header: t('news.sources.colDescription'), sortable: true,  default: true  },
]

const fetchFeeds = async () => {
  loading.value = true
  try {
    const { data } = await api.getRssFeeds()
    feeds.value = Array.isArray(data) ? data : (data?.feeds ?? [])
  } catch (err) {
    notify.error(t('common.error'), err.response?.data?.message || t('news.sources.loadFailed'))
  } finally {
    loading.value = false
  }
}

onMounted(fetchFeeds)

const openCreate = () => {
  isAdding.value = true
  editingFeed.value = { url: '', description: '' }
  showDialog.value = true
}
const openEdit = (row) => {
  isAdding.value = false
  editingFeed.value = { id: row.id, url: row.url ?? '', description: row.description ?? '' }
  showDialog.value = true
}
const closeDialog = () => { showDialog.value = false }

const saveFeed = async () => {
  const { id, ...payload } = editingFeed.value
  if (!payload.url?.trim()) { notify.warn(t('common.hint'), t('news.sources.urlMissing')); return }
  saving.value = true
  try {
    if (isAdding.value) {
      await api.createRssFeed(payload)
      notify.success(t('news.sources.saved'), t('news.sources.feedCreated'))
    } else {
      await api.updateRssFeed(id, payload)
      notify.success(t('news.sources.saved'), t('news.sources.feedUpdated'))
    }
    showDialog.value = false
    await fetchFeeds()
  } catch (err) {
    notify.error(t('news.sources.saveFailed'), err.response?.data?.message || err.message)
  } finally {
    saving.value = false
  }
}

const deleteFeeds = async (rows) => {
  if (!await confirmDelete(t('news.sources.confirmDelete'))) return
  try {
    await Promise.all(rows.map(r => api.deleteRssFeed(r.id)))
    notify.success(t('news.sources.deleted'), t('news.sources.feedDeleted'))
    await fetchFeeds()
  } catch (err) {
    notify.error(t('news.sources.deleteFailed'), err.response?.data?.message || err.message)
  }
}

const tableActions = computed(() => [
  user.can('news.change') && {
    label:          t('news.sources.editTooltip'),
    icon:           'pi pi-pencil',
    requiresSingle: true,
    command:        (rows) => openEdit(rows[0]),
  },
  user.can('news.delete') && {
    label:     t('news.sources.deleteTooltip'),
    icon:      'pi pi-trash',
    severity:  'danger',
    separator: true,
    command:   deleteFeeds,
  },
].filter(Boolean))
</script>

<template>
  <layoutShell>
    <div class="layout-content-wrapper">
      <main class="content">
        <AppTable
          table-key="news-rss-feeds"
          :columns="columns"
          :data="feeds"
          :loading="loading"
          :actions="tableActions"
          @refresh="fetchFeeds"
        >
          <template #toolbar>
            <pButton
              v-if="user.can('news.create')"
              icon="pi pi-plus"
              :label="t('news.sources.addBtn')"
              size="small"
              @click="openCreate"
            />
          </template>
        </AppTable>

        <AppDialog
          v-model="showDialog"
          :title="isAdding ? t('news.sources.dialogCreate') : t('news.sources.dialogEdit')"
          size="md"
          :showFooter="false"
          :maximizable="false"
        >
          <form class="grid" @submit.prevent="saveFeed">
            <div class="col-12">
              <label class="block mb-2">{{ t('news.sources.formUrl') }}</label>
              <InputText v-model.trim="editingFeed.url" class="w-full" placeholder="https://example.com/feed.xml" required />
            </div>
            <div class="col-12">
              <label class="block mb-2">{{ t('news.sources.formDescription') }}</label>
              <InputText v-model.trim="editingFeed.description" class="w-full" :placeholder="t('news.sources.formDescriptionPlaceholder')" />
            </div>
            <div class="col-12 flex justify-end gap-2 mt-2">
              <pButton type="button" :label="t('common.cancel')" severity="secondary" @click="closeDialog" />
              <pButton type="submit" :label="t('common.save')" :loading="saving" />
            </div>
          </form>
        </AppDialog>
      </main>
    </div>

    <div class="layout-footer">
      <LayoutFooter />
    </div>
  </layoutShell>
</template>
